import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

const AUTH_TOKEN_KEY = 'mangogrid.auth.token';
const AUTH_USER_KEY  = 'mangogrid.auth.user';

export type AccountType = 'public' | 'organization';

export interface AuthUser {
  username: string;
  accountType: AccountType;
  orgSlug?: string;
  orgName?: string;
  orgRole?: 'admin' | 'member';
  subscription?: string;
}

interface AuthContextValue {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (nextToken: string, userInfo?: AuthUser) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function loadUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(AUTH_TOKEN_KEY));
  const [user, setUser] = useState<AuthUser | null>(loadUser);

  const login = useCallback((nextToken: string, userInfo?: AuthUser) => {
    localStorage.setItem(AUTH_TOKEN_KEY, nextToken);
    setToken(nextToken);
    if (userInfo) {
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userInfo));
      setUser(userInfo);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    token,
    user,
    isAuthenticated: Boolean(token),
    login,
    logout,
  }), [token, user, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
